import { MTextField, ResponsiveFormContainer } from "@app/components";
import { MODES_OPTIONS } from "@app/types/generics.types";
import { toExponentialUppercase } from "@app/utils/functions";
import { Typography } from "@mui/material";
import React, { useEffect } from "react";
import { useFormContext } from "react-hook-form";

interface AAFeNiUncertaintyProps {
  mode: MODES_OPTIONS;
}

const AAFeNiUncertainty = ({ mode }: AAFeNiUncertaintyProps) => {
  const form = useFormContext();
  const isView = mode === MODES_OPTIONS.VIEW;

  const calculateSquaredValue = (mgFieldName, gFieldName, resultFieldName) => {
    const mgValue = form.watch(mgFieldName);
    if (mgValue !== undefined && mgValue !== null) {
      const gValue = mgValue * 1e-3;
      form.setValue(gFieldName, toExponentialUppercase(gValue, 2));
      const squaredValue = Math.pow(gValue / 2, 2) + Math.pow(gValue / 2, 2);
      form.setValue(resultFieldName, toExponentialUppercase(squaredValue, 2));
    }
  };

  const setFixedValue = (fieldName, resultFieldName, decimals) => {
    const fieldValue = form.watch(fieldName);
    if (fieldValue !== undefined && fieldValue !== null) {
      const sanitizedValue = (fieldValue / 2).toString().replace(/,/g, ".");
      const numericValue = parseFloat(sanitizedValue);
      if (!isNaN(numericValue)) {
        form.setValue(resultFieldName, numericValue.toFixed(decimals));
      } else {
        form.setValue(resultFieldName, "0");
      }
    }
  };

  useEffect(() => {
    calculateSquaredValue(
      "mass_sample_us_k2_mg",
      "mass_sample_us_k2_g",
      "mass_sample_u2s_k1_g2"
    );
    calculateSquaredValue(
      "mass_sample_ub_k2_mg",
      "mass_sample_ub_k2_g",
      "mass_sample_u2b_k1_g2"
    );

    setFixedValue("AA_k2", "AA_k1", 2);
    setFixedValue("NI_k2", "NI_k1", 2);
    setFixedValue("FE_k2", "FE_k1", 3);
    setFixedValue("NI_MG_k2", "NI_MG_k1", 3);
    setFixedValue("NI_FE_k2", "NI_FE_k1", 4);
  }, [form.watch()]); // eslint-disable-line react-hooks/exhaustive-deps

  const renderTextField = (
    name,
    label,
    step = 0.1,
    disabled = false,
    validationRequired = true,
    type = "number"
  ) => (
    <MTextField
      key={name}
      name={name}
      label={label}
      type={type}
      fullWidth
      validation={
        validationRequired ? { required: "El valor es requerido" } : undefined
      }
      disabled={isView || disabled}
      inputProps={{ step }}
    />
  );

  return (
    <>
      <ResponsiveFormContainer columns={4} mt={2}>
        <Typography variant="tagTextPressed" sx={{ whiteSpace: "pre-line" }}>
          {
            "Incertidumbre masa muestra (us) para k=2 (mg) \n (*)Aplica la incertidumbre máxima de la balanza analítica con 100 g"
          }
        </Typography>
        {renderTextField("mass_sample_us_k2_mg", "k=2 us(mg)")}
        {renderTextField("mass_sample_us_k2_g", "k=2 us(g)", 0.001, true)}
        {renderTextField(
          "mass_sample_u2s_k1_g2",
          "<span> k=1 u<sup>2</sup>s (g)<sup>2</sup></span>",
          0.01,
          true
        )}
      </ResponsiveFormContainer>

      <ResponsiveFormContainer columns={4} mt={2}>
        <Typography variant="tagTextPressed" sx={{ whiteSpace: "pre-line" }}>
          {
            "Incertidumbre masa de b (alícuota de s) (ub) para k=2 (mg)  \n (*)Aplica la incertidumbre máxima de la balanza analítica con 10 g"
          }
        </Typography>
        {renderTextField("mass_sample_ub_k2_mg", "k=2 ub(mg)")}
        {renderTextField("mass_sample_ub_k2_g", "k=2 ub(g)", 0.001, true)}
        {renderTextField(
          "mass_sample_u2b_k1_g2",
          "<span> k=1 u<sup>2</sup>b(g)<sup>2</sup></span>",
          0.01,
          true
        )}
      </ResponsiveFormContainer>

      <ResponsiveFormContainer columns={4} mt={2}>
        <Typography variant="tagTextPressed" sx={{ whiteSpace: "pre-line" }}>
          {"Incertidumbre medida de Fe por AA en %"}
        </Typography>
        {renderTextField("AA_k2", "k=2")}
        {renderTextField("AA_k1", "k=1", 0.01, true, false, "text")}{" "}
        {/* Cambiado a text */}
      </ResponsiveFormContainer>

      <ResponsiveFormContainer columns={4} mt={2}>
        <Typography variant="tagTextPressed" sx={{ whiteSpace: "pre-line" }}>
          {"Incertidumbre medida de Ni por AA en %"}
        </Typography>
        {renderTextField("NI_k2", "k=2")}
        {renderTextField("NI_k1", "k=1", 0.01, true, false, "text")}{" "}
        {/* Cambiado a text */}
      </ResponsiveFormContainer>

      <ResponsiveFormContainer columns={4} mt={2}>
        <Typography variant="tagTextPressed" sx={{ whiteSpace: "pre-line" }}>
          {"Incertidumbre Concentración patrón de Fe (mg/l) para k=2"}
        </Typography>
        {renderTextField("FE_k2", "k=2")}
        {renderTextField("FE_k1", "k=1", 0.001, true, false, "text")}{" "}
        {/* Cambiado a text */}
      </ResponsiveFormContainer>

      <ResponsiveFormContainer columns={4} mt={2}>
        <Typography variant="tagTextPressed" sx={{ whiteSpace: "pre-line" }}>
          {"Incertidumbre Concentración patrón de Ni (mg/l) para k=2"}
        </Typography>
        {renderTextField("NI_MG_k2", "k=2")}
        {renderTextField("NI_MG_k1", "k=1", 0.001, true, false, "text")}{" "}
        {/* Cambiado a text */}
      </ResponsiveFormContainer>

      <ResponsiveFormContainer columns={4} mt={2}>
        <Typography variant="tagTextPressed" sx={{ whiteSpace: "pre-line" }}>
          {
            "Incertidumbre del Volumen del patrón de Fe y Ni (ml) para k=2  \n (*) Se corresponde con la incertidumbre total para la pipeta automática de 1 ml"
          }
        </Typography>
        {renderTextField("NI_FE_k2", "k=2")}
        {renderTextField("NI_FE_k1", "k=1", 0.001, true, false, "text")}{" "}
        {/* Cambiado a text */}
      </ResponsiveFormContainer>
    </>
  );
};

export default AAFeNiUncertainty;
